import { createSlice, isAnyOf, PayloadAction } from "@reduxjs/toolkit";
import MD5 from "crypto-js/md5";
import dayjs from "dayjs";
import {
    getCatalogExportThunk,
    getCompanyDebtListByOwnerMarketIdThunk,
    getContractListThunk,
    getContractTypesListThunk,
    getFullUnassignedPlanByRouteIdThunk,
    getKpiGetMarketProgressThunk,
    getMarketInventoryGetThunk,
    getMotivationInfoThunk,
    getPlanGetByMarketIdThunk,
    getPriceListByMarketThunk,
    getStorageInventoryGetThunk,
    getWorkGetScheduleThunk,
    getWorkStartAndGetScheduleThunk,
    PostOrderSaveParams,
} from "tm-shared/api";
import { Debt, Price } from "tm-shared/types";
import { arrayToRecord, uniqueByKey } from "tm-shared/utils";
import { normalizeNestedObjectMap, normalizeObjectMap } from "tm-shared/utils";

import { QueueProgress, QueueTaskStatus } from "@/hooks/useAsyncTaskQueue";
import { makeCommand } from "@/hooks/useRequestQueue";
import { RequestQueueItem } from "@/types/requestQueue";

import {
    getKpiGetMarketProgressesThunk,
    getPlansByMarketIdThunk,
} from "./offline.slice.thunks";
import { OfflineState } from "./offline.slice.types";

function getInitialState(): OfflineState {
    return {
        offlineMode: false,
        manualOfflineMode: false,
        showOnlinePopup: false,
        products: {},
        debts: {},
        debtsByFOP: {},
        marketsPriceLists: {},
        priceLists: {},
        inventoryList: {},
        targetsContracts: {},
        promoList: {},
        promosVocabulary: {},
        ignoreRemainingsOrders: {},
        filteredParams: [],
        contractTypesList: {},
        marketsInventories: {},
        plans: {},
        facts: {},
        motivationInfoDate: "",
        latency: 0,
        lastOfflineUpdate: null,
        shouldUpdateKpi: false,
        isUnscheduledMarketsProcessing: false,
        isInitialDataLoading: true,

        requestQueueStatus: {
            isRunning: false,
            lastTickAt: null,
        },
        requestQueue: [],

        photoUploadRetry: {
            nextPhotoUploadRetryAt: null,
            candidatesCount: 0,
            ignoreBackoffOnce: false,
            progress: {
                tasks: [],
                isRunning: false,
                completedCount: 0,
                totalCount: 0,
            },
        },

        progress: {
            lastModifiedAt: "",
            lastSyncedAt: "",
        },

        reminderTimestamps: {
            checkout: "",
            task: "",
        },
    };
}

const initialState: OfflineState = getInitialState();

export const offlineSlice = createSlice({
    name: "offline",
    initialState,
    reducers: {
        updateOfflineStatus(state, action: PayloadAction<boolean>) {
            state.offlineMode = action.payload;
        },
        updateManualOfflineStatus(state, action) {
            state.manualOfflineMode = action.payload;
        },

        setLatency(state, action: PayloadAction<number | "offline">) {
            state.latency = action.payload;
        },
        setShowOnlinePopup(state, action: PayloadAction<boolean>) {
            state.showOnlinePopup = action.payload;
        },

        addIgnoreRemainingOrder(
            state,
            action: PayloadAction<{ orderId: string; barcode: string }>,
        ) {
            const { orderId, barcode } = action.payload;

            const existingBarcodes = state.ignoreRemainingsOrders[orderId];

            if (!existingBarcodes) {
                state.ignoreRemainingsOrders[orderId] = [barcode];
            } else if (!existingBarcodes.includes(barcode)) {
                existingBarcodes.push(barcode);
            }
        },

        removeIgnoreRemainingOrders(state, action: PayloadAction<string[]>) {
            action.payload.forEach((orderId) => {
                delete state.ignoreRemainingsOrders[orderId];
            });
        },

        setLastOfflineUpdate(state, action: PayloadAction<string>) {
            state.lastOfflineUpdate = action.payload;
        },

        setShouldUpdateKpi(state, action: PayloadAction<boolean>) {
            state.shouldUpdateKpi = action.payload;
        },
        setUpdateUnscheduledMarketsProcessing(
            state,
            action: PayloadAction<boolean>,
        ) {
            state.isUnscheduledMarketsProcessing = action.payload;
        },
        setIsInitialDataLoading(state, action: PayloadAction<boolean>) {
            state.isInitialDataLoading = action.payload;
        },

        // -------------------- Request Queue -------------------

        setRequestQueueRunning(state, action: PayloadAction<boolean>) {
            const status = action.payload;

            state.requestQueueStatus.isRunning = status;

            if (status) {
                state.requestQueueStatus.lastTickAt = Date.now();
            } else {
                state.requestQueueStatus.lastTickAt = null;
            }
        },

        touchRequestQueue(state) {
            state.requestQueueStatus.lastTickAt = Date.now();
        },

        bulkAddRequestQueueItems(
            state,
            action: PayloadAction<RequestQueueItem[]>,
        ) {
            const normalizeQueue = uniqueByKey(
                [...state.requestQueue, ...action.payload],
                "id",
            );

            state.requestQueue = normalizeQueue;
        },

        addRequestQueueItem: (
            state,
            action: PayloadAction<
                Omit<RequestQueueItem, "status" | "createdAt">
            >,
        ) => {
            const incoming = action.payload;

            const currentIndex = state.requestQueue.findIndex(
                (q) => q.id === incoming.id,
            );

            if (currentIndex !== -1) {
                const current = state.requestQueue[currentIndex];

                current.commands.push(...incoming.commands);
                current.metadata = {
                    ...current.metadata,
                    ...incoming.metadata,
                };
                current.options = {
                    ...current?.options,
                    ...incoming?.options,
                };
                current.status = QueueTaskStatus.Pending;
            } else {
                state.requestQueue.push({
                    ...incoming,
                    status: QueueTaskStatus.Pending,
                    createdAt: dayjs().toISOString(),
                });
            }
        },

        addOrReplaceRequestQueueItem: (
            state,
            action: PayloadAction<
                Omit<RequestQueueItem, "status" | "createdAt">
            >,
        ) => {
            const newItem = {
                ...action.payload,
                status: QueueTaskStatus.Pending,
                createdAt: dayjs().toISOString(),
            };

            const currentIndex = state.requestQueue.findIndex(
                (q) => q.id === newItem.id,
            );

            if (currentIndex !== -1) {
                state.requestQueue[currentIndex] = newItem;
            } else {
                state.requestQueue.push(newItem);
            }
        },

        upsertSaleOrderUpdateRequestQueueItem: (
            state,
            action: PayloadAction<{
                orderId: string;
                orderPayload: PostOrderSaveParams;
            }>,
        ) => {
            const { orderId, orderPayload } = action.payload;

            const idx = state.requestQueue.findIndex(
                (item) => item?.metadata?.orderId === orderId,
            );

            if (idx === -1) {
                state.requestQueue.push({
                    id: orderId,
                    metadata: {
                        orderId,
                        title: `Оновлення даних замовлення: ${orderId}`,
                    },
                    createdAt: dayjs().toISOString(),
                    status: QueueTaskStatus.Pending,
                    commands: [
                        makeCommand({
                            type: "postOrderSave",
                            title: `Збереження змін`,
                            params: orderPayload,
                        }),
                    ],
                });
                return;
            }

            const current = state.requestQueue[idx];
            current.status = QueueTaskStatus.Pending;

            const currentOrderCmdIndex = current.commands.findIndex(
                (c) => c.type === "postOrderSave",
            );

            if (currentOrderCmdIndex === -1) {
                current.commands.push(
                    makeCommand({
                        type: "postOrderSave",
                        title: `Збереження змін`,
                        params: orderPayload,
                    }),
                );
                return;
            }

            current.commands[currentOrderCmdIndex].params = orderPayload;
        },

        deleteRequestQueueItem: (state, action: PayloadAction<string>) => {
            state.requestQueue = state.requestQueue.filter(
                (i) => i.id !== action.payload,
            );
        },

        patchRequestQueueItem: (
            state,
            action: PayloadAction<{
                id: string;
                patch: Partial<Pick<RequestQueueItem, "status" | "progress">>;
            }>,
        ) => {
            const { id, patch } = action.payload;

            const idx = state.requestQueue.findIndex((i) => i.id === id);
            if (idx === -1) return;

            Object.assign(state.requestQueue[idx], patch);
        },

        restartRequestQueue: (
            state,
            action: PayloadAction<string | undefined>,
        ) => {
            const id = action.payload;

            const restartItem = (item: RequestQueueItem) => {
                item.status = QueueTaskStatus.Pending;
            };

            if (id) {
                const found = state.requestQueue.find((x) => x.id === id);
                if (found) restartItem(found);
            } else {
                state.requestQueue.map(restartItem);
            }
        },

        stopRequestQueue: (state) => {
            state.requestQueue = state.requestQueue.map((item) => ({
                ...item,
                status: QueueTaskStatus.Idle,
            }));
        },

        clearRequestQueue: (state) => {
            state.requestQueue = [];
        },

        normalizeRequestQueueTail: (state) => {
            const queue = state.requestQueue;

            const TAIL_CHECKOUT_ID = "tail:updateCheckout";
            const TAIL_TASKS_ID = "tail:refreshTasks";

            const hasCommand = (types: string[]) =>
                queue.some((item) =>
                    item.commands.some((c) => types.includes(c.type)),
                );

            const needCheckoutTail = hasCommand([
                "postOrderSave",
                "postOrderSaveOne",
            ]);
            const needTasksTail = hasCommand([
                "postTaskAddSaleTask",
                "postAddDistributionPrices",
                "postTaskAddDebtTask",
                "postTaskAddRejectTask",
                "postTaskComplete",
            ]);

            const takeOrCreateTail = (
                id: string,
                create: () => RequestQueueItem,
            ) => {
                const idx = queue.findIndex((x) => x.id === id);
                if (idx === -1) return create();
                const [it] = queue.splice(idx, 1);
                return it;
            };

            if (!needCheckoutTail && !needTasksTail) return;

            const checkoutTail = needCheckoutTail
                ? takeOrCreateTail(TAIL_CHECKOUT_ID, () => ({
                      id: TAIL_CHECKOUT_ID,
                      status: QueueTaskStatus.Pending,
                      metadata: {
                          title: "Синхронізація даних (кошик)",
                      },
                      createdAt: dayjs().toISOString(),
                      options: {
                          todayOnly: true,
                      },
                      commands: [
                          makeCommand({
                              type: "updateCheckout",
                              title: "Завантаження даних кошику",
                              params: null,
                          }),
                      ],
                  }))
                : null;

            const tasksTail = needTasksTail
                ? takeOrCreateTail(TAIL_TASKS_ID, () => ({
                      id: TAIL_TASKS_ID,
                      status: QueueTaskStatus.Pending,
                      metadata: {
                          title: "Оновлення списку задач",
                      },
                      createdAt: dayjs().toISOString(),
                      options: {
                          todayOnly: true,
                      },
                      commands: [
                          makeCommand({
                              type: "getTaskListsThunk",
                              title: "Завантаження актуального списку задач",
                              params: null,
                          }),
                      ],
                  }))
                : null;

            if (checkoutTail) queue.push(checkoutTail);
            if (tasksTail) queue.push(tasksTail);
        },

        // -----------------------------------------------------

        setMarketInventories: (
            state,
            action: PayloadAction<{
                marketId: string;
                rest: Record<string, number>;
            }>,
        ) => {
            const { marketId, rest } = action.payload;

            state.marketsInventories[marketId] = rest;
        },

        deductInventory: (
            state,
            action: PayloadAction<{
                storageId: string;
                newInventory: {
                    [barcode: string]: number;
                };
            }>,
        ) => {
            const { storageId, newInventory } = action.payload;
            const currentStorage = state?.inventoryList?.[storageId] ?? {};
            for (const [barcode, count] of Object.entries(newInventory)) {
                const currentInventory = currentStorage?.[barcode] ?? 0;
                const newCount = currentInventory - count;

                currentStorage[barcode] = Math.max(0, newCount);
            }
        },

        // ----------------------------------- Photo upload retry
        setNextPhotoUploadRetryAt: (
            state,
            action: PayloadAction<{ time: string; ignoreBackoff?: boolean }>,
        ) => {
            const { time, ignoreBackoff = false } = action.payload;
            state.photoUploadRetry.nextPhotoUploadRetryAt = time;
            state.photoUploadRetry.ignoreBackoffOnce = ignoreBackoff;
        },
        setPhotoUploadRetryProgress: (
            state,
            action: PayloadAction<QueueProgress>,
        ) => {
            state.photoUploadRetry.progress = action.payload;
        },
        setPhotoUploadRetryCandidatesCount: (
            state,
            action: PayloadAction<number>,
        ) => {
            state.photoUploadRetry.candidatesCount = action.payload;
        },

        // --------------------------------------- Progress sync
        setProgressLastSyncedAt(state, action: PayloadAction<string>) {
            state.progress.lastSyncedAt = action.payload;
        },

        setProgressLastModifiedAt(state, action: PayloadAction<string>) {
            state.progress.lastModifiedAt = action.payload;
        },

        // -------------------------------------- Reminder
        setCheckoutReminder(state, { payload }: PayloadAction<string>) {
            state.reminderTimestamps.checkout = payload;
        },
        setTaskReminder(state, { payload }: PayloadAction<string>) {
            state.reminderTimestamps.task = payload;
        },
    },
    extraReducers: (builder) => {
        builder

            .addCase(getContractTypesListThunk.fulfilled, (state, action) => {
                state.contractTypesList = action.payload?.result;
            })
            .addCase(getCatalogExportThunk.fulfilled, (state, action) => {
                state.filteredParams = action.payload.result.filteredParams;

                const normalizedProducts = action.payload.result.products.map(
                    (p) => {
                        return {
                            name: p.name,
                            barcode: p.barcode,
                            params: p.params,
                            marketingName: p.marketingName,
                        };
                    },
                );

                state.products = arrayToRecord(normalizedProducts, "barcode");
            })
            .addCase(getContractListThunk.fulfilled, (state, action) => {
                const newContractsMap = action.payload?.result?.reduce(
                    (acc, contract) => {
                        const targetId = contract.targetId;

                        if (!acc[targetId]) {
                            acc[targetId] = [];
                        }

                        acc[targetId].push(contract);

                        return acc;
                    },
                    {} as Record<
                        string,
                        (typeof action.payload.result)[number][]
                    >,
                );

                Object.entries(newContractsMap).forEach(
                    ([targetId, contracts]) => {
                        state.targetsContracts[targetId] = contracts;
                    },
                );
            })
            .addCase(
                getStorageInventoryGetThunk.fulfilled,
                (state, { payload, meta }) => {
                    state.inventoryList[meta.arg.storageId] =
                        normalizeObjectMap(payload.result);
                },
            )
            .addCase(
                getCompanyDebtListByOwnerMarketIdThunk.fulfilled,
                (state, action) => {
                    const marketId = action.meta.arg.marketId;

                    state.debtsByFOP[marketId] = {
                        ...action.payload.result,
                    };
                },
            )
            .addCase(
                getMarketInventoryGetThunk.fulfilled,
                (state, { payload }) => {
                    const { marketId, rest } = payload.result;

                    state.marketsInventories[marketId] =
                        normalizeObjectMap(rest);
                },
            )
            .addCase(
                getPlansByMarketIdThunk.fulfilled,
                (state, { payload }) => {
                    state.plans = {
                        ...payload,
                    };
                },
            )
            .addCase(
                getPlanGetByMarketIdThunk.fulfilled,
                (state, { payload }) => {
                    if (!payload.result) return;
                    state.plans = {
                        ...state.plans,
                        [payload.result.targetId]:
                            payload.result.settings.items,
                    };
                },
            )

            .addCase(
                getKpiGetMarketProgressesThunk.fulfilled,
                (state, { payload }) => {
                    state.facts = {
                        ...payload,
                    };
                },
            )
            .addCase(
                getKpiGetMarketProgressThunk.fulfilled,
                (state, { payload, meta }) => {
                    if (!payload.result) return;
                    state.facts = {
                        ...state.facts,
                        [meta.arg.marketId]: arrayToRecord(
                            payload.result,
                            "barcode",
                        ),
                    };
                },
            )

            .addCase(
                getFullUnassignedPlanByRouteIdThunk.fulfilled,
                (state, { payload }) => {
                    if (!payload.result) return;
                    state.plans = {
                        ...state.plans,
                        unassignedPlan: payload.result.settings.items,
                    };
                },
            )
            .addCase(getMotivationInfoThunk.fulfilled, (state, { payload }) => {
                const result = payload?.result?.state.groups;

                if (!result) return;
                state.motivationInfoDate = payload?.result?.startDate;
            })
            .addCase(
                getPriceListByMarketThunk.fulfilled,
                (
                    state,
                    {
                        payload: { result },
                        meta: {
                            arg: { marketId },
                        },
                    },
                ) => {
                    const hash = MD5(JSON.stringify(result)).toString();

                    state.marketsPriceLists[marketId] = hash;

                    const byBarcode = result.reduce<Record<string, Price>>(
                        (acc, item) => {
                            acc[item.barcode] = item;
                            return acc;
                        },
                        {},
                    );

                    state.priceLists[hash] = byBarcode;
                },
            );

        builder.addMatcher(
            isAnyOf(
                getWorkGetScheduleThunk.fulfilled,
                getWorkStartAndGetScheduleThunk.fulfilled,
            ),
            (state, { payload }) => {
                const {
                    marketsPriceList,
                    pricelists,
                    debtList,
                    marketsContractList,
                    marketsInventories,
                    marketsPromoActual,
                    marketsPartnerInventories,
                    promos,
                } = payload.result;

                state.marketsPriceLists = marketsPriceList ?? {};

                state.priceLists = Object.entries(pricelists ?? {}).reduce(
                    (acc, [key, list]) => {
                        acc[key] = Object.values(list).reduce<
                            Record<string, Price>
                        >((inner, pl) => {
                            inner[pl.barcode] = pl;
                            return inner;
                        }, {});
                        return acc;
                    },
                    {} as Record<string, Record<string, Price>>,
                );

                Object.entries(debtList ?? {})?.forEach(([key, value]) => {
                    state.debts[key] = value?.reduce(
                        (acc, debt) => {
                            acc[debt.consignmentId] = debt;
                            return acc;
                        },
                        {} as Record<string, Debt>,
                    );
                });

                state.targetsContracts = marketsContractList;
                state.marketsInventories =
                    normalizeNestedObjectMap(marketsInventories);
                state.promoList = promos;
                state.promosVocabulary = marketsPromoActual;
                state.inventoryList = normalizeNestedObjectMap(
                    marketsPartnerInventories,
                );
            },
        );
    },
});

export const {
    updateOfflineStatus,
    setShowOnlinePopup,
    addIgnoreRemainingOrder,
    removeIgnoreRemainingOrders,
    updateManualOfflineStatus,
    setLatency,
    setLastOfflineUpdate,
    setIsInitialDataLoading,
    setShouldUpdateKpi,
    setUpdateUnscheduledMarketsProcessing,
    setMarketInventories,
    deductInventory,

    // ------ RequestQueue
    addRequestQueueItem,
    bulkAddRequestQueueItems,
    deleteRequestQueueItem,
    patchRequestQueueItem,
    addOrReplaceRequestQueueItem,
    upsertSaleOrderUpdateRequestQueueItem,
    restartRequestQueue,
    stopRequestQueue,
    normalizeRequestQueueTail,
    clearRequestQueue,
    setRequestQueueRunning,
    touchRequestQueue,

    // ------ Photo upload retry
    setNextPhotoUploadRetryAt,
    setPhotoUploadRetryProgress,
    setPhotoUploadRetryCandidatesCount,

    // ------ Store sync
    setProgressLastModifiedAt,
    setProgressLastSyncedAt,

    // ------ Reminder
    setCheckoutReminder,
    setTaskReminder,
} = offlineSlice.actions;

export default offlineSlice.reducer;
